import React from 'react';
import styled from 'astroturf/react';

import { BundleWork } from './types';
import { WorkCard } from './WorkCard';

/**
 * V6 (`bundle_grid_v6`) replacement for the vertical `PaperPreviewList`.
 * Renders the 6 visible papers in a 3×2 grid (2×3 on mobile) so the
 * bundle reads as "I get six things" rather than "papers to scroll."
 *
 * Cards use the existing `compact` mode on `WorkCard` to keep each cell
 * tight. The optional `includedWork` (source paper) is rendered first
 * if provided.
 *
 * Footer (`FadeFooter` or `BlurredHiddenThumbs`) is rendered below the
 * grid by the caller and not handled here — the grid is just the cards.
 */
export interface BundleGridProps {
  works: ReadonlyArray<BundleWork>;
  includedWork?: BundleWork;
  showAnnotations?: boolean;
  showRelevancy?: boolean;
  footer?: React.ReactNode;
  /** Rendered in place of the grid when `works` is empty AND no
   *  `includedWork` is provided. Mirrors `PaperPreviewList`'s shape so
   *  both renderers share the same empty-filter UX. */
  emptyState?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function BundleGrid({
  works,
  includedWork,
  showAnnotations = false,
  showRelevancy = true,
  footer,
  emptyState,
  className,
  style,
}: BundleGridProps) {
  const showEmptyState = works.length === 0 && !includedWork && emptyState;
  return (
    <Container className={className} style={style}>
      {showEmptyState ? (
        emptyState
      ) : (
        <Grid>
          {includedWork && (
            <WorkCard
              work={includedWork}
              showRelevancy={false}
              showAnnotations={showAnnotations}
              compact
            />
          )}
          {works.map((work) => (
            <WorkCard
              key={work.id}
              work={work}
              showRelevancy={showRelevancy}
              showAnnotations={showAnnotations}
              compact
            />
          ))}
        </Grid>
      )}
      {footer}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-xs-12, 12px);
  padding-right: 6px;

  @media (max-width: 567px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
